import React, { useRef, useState } from "react";
import { UploadCloud, FileCheck2, X } from "lucide-react";

const fmtSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const FileDropzone = ({
  file,
  onFile,
  accept = ".pdf",
  maxSize = 50 * 1024 * 1024,
  hint = "PDF fino a 50MB • Documenti digitali, scansioni e foto",
  title = "Trascina qui il tuo PDF",
  onError,
}) => {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);

  const validate = (f) => {
    if (!f) return;
    const ext = accept.split(",").map((s) => s.trim().toLowerCase());
    const ok = ext.some((e) => f.name.toLowerCase().endsWith(e));
    if (!ok) {
      onError?.(`Formato non valido. Sono ammessi: ${accept}`);
      return;
    }
    if (f.size > maxSize) {
      onError?.("Il file supera il limite di 50MB");
      return;
    }
    onFile(f);
  };

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        data-testid="file-input"
        className="hidden"
        onChange={(e) => validate(e.target.files?.[0])}
      />
      {!file ? (
        <div
          data-testid="dropzone"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDrag(true);
          }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDrag(false);
            validate(e.dataTransfer.files?.[0]);
          }}
          className={`group flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed px-6 py-14 text-center transition-all ${
            drag
              ? "border-emerald-500 bg-emerald-50"
              : "border-slate-300 bg-slate-50 hover:border-emerald-400 hover:bg-emerald-50/40"
          }`}
        >
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-600 transition-transform group-hover:scale-105">
            <UploadCloud className="h-8 w-8" />
          </div>
          <p className="font-display text-xl font-bold text-slate-900">
            {drag ? "Rilascia il file per iniziare" : title}
          </p>
          <p className="mt-1 text-sm text-slate-500">
            oppure clicca per selezionarlo dal computer
          </p>
          <p className="mt-4 text-xs font-medium text-slate-400">{hint}</p>
        </div>
      ) : (
        <div
          data-testid="selected-file-card"
          className="flex items-center justify-between rounded-2xl border border-emerald-200 bg-white p-5 shadow-sm"
        >
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-100 text-emerald-600">
              <FileCheck2 className="h-6 w-6" />
            </div>
            <div>
              <p className="font-semibold text-slate-900" data-testid="selected-file-name">
                {file.name}
              </p>
              <p className="text-sm text-slate-500">{fmtSize(file.size)}</p>
            </div>
          </div>
          <button
            data-testid="remove-file-button"
            onClick={() => onFile(null)}
            className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
            aria-label="Rimuovi file"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      )}
    </div>
  );
};
